import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  Key,
  Loader2,
  Mail,
  Plus,
  Shield,
  User,
  UserMinus,
  Users,
  Eye,
  EyeOff,
} from 'lucide-react';
import {
  fetchPortalUsers,
  createPortalUser,
  deactivatePortalUser,
  resetPortalUserPassword,
  type PortalUser,
} from '@/lib/api';
import { useToast } from '@/contexts/ToastContext';
import { formatDate } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface PortalUserManagerProps {
  companyId: string;
}

export function PortalUserManager({ companyId }: PortalUserManagerProps) {
  const { addToast } = useToast();
  const queryClient = useQueryClient();

  const [showAddDialog, setShowAddDialog] = useState(false);
  const [deactivateConfirmId, setDeactivateConfirmId] = useState<string | null>(null);
  const [resetPasswordUserId, setResetPasswordUserId] = useState<string | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [resetPasswordError, setResetPasswordError] = useState<string | null>(null);
  const [showResetPassword, setShowResetPassword] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['portal-users', companyId],
    queryFn: () => fetchPortalUsers({ companyId }),
  });

  const portalUsers = data?.data ?? [];

  const deactivateMutation = useMutation({
    mutationFn: deactivatePortalUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['portal-users', companyId] });
      addToast({ type: 'success', title: 'Portal user deactivated' });
      setDeactivateConfirmId(null);
    },
    onError: (err: Error) => {
      addToast({
        type: 'error',
        title: 'Failed to deactivate user',
        message: err.message,
      });
    },
  });

  const resetPasswordMutation = useMutation({
    mutationFn: ({ id, password }: { id: string; password: string }) =>
      resetPortalUserPassword(id, password),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['portal-users', companyId] });
      addToast({ type: 'success', title: 'Password reset successfully' });
      setResetPasswordUserId(null);
      setNewPassword('');
      setResetPasswordError(null);
    },
    onError: (err: Error) => {
      setResetPasswordError(err.message);
      addToast({
        type: 'error',
        title: 'Failed to reset password',
        message: err.message,
      });
    },
  });

  const handleResetPassword = () => {
    if (!resetPasswordUserId) return;
    setResetPasswordError(null);

    if (newPassword.length < 8) {
      setResetPasswordError('Password must be at least 8 characters');
      return;
    }

    resetPasswordMutation.mutate({ id: resetPasswordUserId, password: newPassword });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-text-primary flex items-center gap-2">
            <Shield className="h-5 w-5 text-emerald-500" />
            Client Portal Users
          </h3>
          <p className="text-sm text-text-tertiary mt-1">
            Manage portal access for this company's team members.
          </p>
        </div>
        <Button onClick={() => setShowAddDialog(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Add Portal User
        </Button>
      </div>

      {/* Users list */}
      {isLoading ? (
        <div className="space-y-3">
          {[1, 2].map((i) => (
            <div key={i} className="flex items-center gap-4 p-4 bg-bg-tertiary rounded-lg border border-white/10">
              <Skeleton className="h-10 w-10 rounded-full bg-white/5" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-4 w-40 bg-white/5" />
                <Skeleton className="h-3 w-56 bg-white/5" />
              </div>
              <Skeleton className="h-6 w-16 rounded-full bg-white/5" />
            </div>
          ))}
        </div>
      ) : portalUsers.length === 0 ? (
        <div className="text-center py-12 bg-bg-tertiary rounded-lg border border-white/10">
          <Users className="h-12 w-12 mx-auto text-text-tertiary mb-4" />
          <p className="text-text-secondary font-medium">No portal users</p>
          <p className="text-sm text-text-tertiary mt-1 mb-4">
            Add portal users so the company can view submissions and provide feedback.
          </p>
          <Button onClick={() => setShowAddDialog(true)} variant="outline">
            <Plus className="h-4 w-4 mr-2" />
            Add First Portal User
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {portalUsers.map((user: PortalUser) => (
            <div
              key={user.id}
              className="flex items-center gap-4 p-4 bg-bg-tertiary rounded-lg border border-white/10"
            >
              {/* Avatar */}
              <div className="h-10 w-10 rounded-full bg-bg-elevated flex items-center justify-center flex-shrink-0">
                <User className="h-5 w-5 text-text-tertiary" />
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="font-medium text-text-primary">
                    {user.firstName} {user.lastName}
                  </p>
                  <Badge
                    className={
                      user.isActive
                        ? 'bg-green-500/20 text-green-400 border-green-500/30'
                        : 'bg-red-500/20 text-red-400 border-red-500/30'
                    }
                  >
                    {user.isActive ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
                <div className="flex items-center gap-1.5 mt-0.5">
                  <Mail className="h-3.5 w-3.5 text-text-tertiary" />
                  <p className="text-sm text-text-tertiary truncate">{user.email}</p>
                </div>
                <p className="text-xs text-text-tertiary mt-0.5">
                  Added {formatDate(user.createdAt)}
                </p>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2 flex-shrink-0">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setResetPasswordUserId(user.id);
                    setNewPassword('');
                    setResetPasswordError(null);
                  }}
                  className="text-text-secondary"
                >
                  <Key className="h-3.5 w-3.5 mr-1.5" />
                  Reset Password
                </Button>
                {user.isActive && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setDeactivateConfirmId(user.id)}
                    className="text-red-400 border-red-500/30 hover:bg-red-500/10"
                  >
                    <UserMinus className="h-3.5 w-3.5 mr-1.5" />
                    Deactivate
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Portal User Dialog */}
      <AddPortalUserDialog
        open={showAddDialog}
        onOpenChange={setShowAddDialog}
        companyId={companyId}
      />

      {/* Deactivate Confirmation Dialog */}
      <Dialog
        open={!!deactivateConfirmId}
        onOpenChange={(open) => !open && setDeactivateConfirmId(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Deactivate Portal User</DialogTitle>
            <DialogDescription>
              Are you sure you want to deactivate this portal user? They will no longer be
              able to log into the client portal.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeactivateConfirmId(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                if (deactivateConfirmId) {
                  deactivateMutation.mutate(deactivateConfirmId);
                }
              }}
              disabled={deactivateMutation.isPending}
            >
              {deactivateMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deactivating...
                </>
              ) : (
                'Deactivate User'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog
        open={!!resetPasswordUserId}
        onOpenChange={(open) => {
          if (!open) {
            setResetPasswordUserId(null);
            setNewPassword('');
            setResetPasswordError(null);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="h-5 w-5 text-emerald-500" />
              Reset Password
            </DialogTitle>
            <DialogDescription>
              Set a new password for this portal user. They will need to use this password
              on their next login.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {resetPasswordError && (
              <div className="flex items-center gap-2 p-3 text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-md">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                <span>{resetPasswordError}</span>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="new-password">New Password</Label>
              <div className="relative">
                <Input
                  id="new-password"
                  type={showResetPassword ? 'text' : 'password'}
                  placeholder="Enter new password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  minLength={8}
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  onClick={() => setShowResetPassword(!showResetPassword)}
                  tabIndex={-1}
                >
                  {showResetPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <p className="text-xs text-text-tertiary">Must be at least 8 characters</p>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setResetPasswordUserId(null);
                setNewPassword('');
                setResetPasswordError(null);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleResetPassword}
              disabled={resetPasswordMutation.isPending || !newPassword}
            >
              {resetPasswordMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Resetting...
                </>
              ) : (
                'Reset Password'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

interface AddPortalUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  companyId: string;
}

function AddPortalUserDialog({
  open,
  onOpenChange,
  companyId,
}: AddPortalUserDialogProps) {
  const { addToast } = useToast();
  const queryClient = useQueryClient();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createMutation = useMutation({
    mutationFn: () =>
      createPortalUser({
        companyId,
        email,
        password,
        firstName,
        lastName,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['portal-users', companyId] });
      addToast({ type: 'success', title: 'Portal user created' });
      resetForm();
      onOpenChange(false);
    },
    onError: (err: Error) => {
      setError(err.message);
      addToast({
        type: 'error',
        title: 'Failed to create portal user',
        message: err.message,
      });
    },
  });

  const resetForm = () => {
    setFirstName('');
    setLastName('');
    setEmail('');
    setPassword('');
    setError(null);
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      resetForm();
    }
    onOpenChange(isOpen);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!firstName.trim()) {
      setError('First name is required');
      return;
    }
    if (!lastName.trim()) {
      setError('Last name is required');
      return;
    }
    if (!email.trim()) {
      setError('Email is required');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    createMutation.mutate();
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-emerald-500" />
            Add Portal User
          </DialogTitle>
          <DialogDescription>
            Create a new client portal account. The user will be able to view submissions
            and provide feedback for their company.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="flex items-center gap-2 p-3 text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-md">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="portal-firstName">First Name *</Label>
              <Input
                id="portal-firstName"
                placeholder="John"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="portal-lastName">Last Name *</Label>
              <Input
                id="portal-lastName"
                placeholder="Smith"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="portal-email">Email *</Label>
            <Input
              id="portal-email"
              type="email"
              placeholder="john@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="portal-password">Password *</Label>
            <div className="relative">
              <Input
                id="portal-password"
                type={showPassword ? 'text' : 'password'}
                placeholder="Create a strong password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={8}
              />
              <button
                type="button"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                onClick={() => setShowPassword(!showPassword)}
                tabIndex={-1}
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            <p className="text-xs text-text-tertiary">Must be at least 8 characters</p>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={createMutation.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={createMutation.isPending}>
              {createMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Portal User'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
